/**
 * dd-collection-grid.js
 *
 * "Load more" handler for sections/dd-collection-grid.liquid.
 *
 * Flow:
 *   1. User clicks the button rendered under a paginated grid.
 *   2. We hit `{pathname}?page={N}&section_id={sectionId}`, which asks
 *      Shopify's Section Rendering API to re-render our section at
 *      page N of its internal `paginate` loop.
 *   3. We parse the returned HTML, extract the new `<li>` nodes from
 *      the response's grid, and append them to the live grid.
 *   4. If the response includes a new load-more button (i.e. there's
 *      still a `paginate.next`), we swap in its `data-next-url`;
 *      otherwise we remove the button entirely.
 *   5. We dispatch `dd:cards:appended` so any companion hydration
 *      scripts (rating pill fill, future swatch/variant enhancers)
 *      can pick up the newly-inserted cards.
 *
 * Why the Section Rendering API instead of /products.json?
 *   - The JSON API loses all our Liquid-rendered markup (swatches,
 *     discount pills, rating, variant metafield images). Re-rendering
 *     in JS would duplicate the snippet logic.
 *   - Shopify's Section API returns the already-rendered HTML for
 *     this specific section, so the new cards are byte-for-byte
 *     identical to the initial server-rendered ones.
 */
(function () {
  'use strict';

  var BTN_SELECTOR = '[data-load-more]';

  function onClick(event) {
    var btn = event.target.closest(BTN_SELECTOR);
    if (!btn) return;
    if (btn.hasAttribute('aria-busy')) return;

    event.preventDefault();

    var nextUrl = btn.getAttribute('data-next-url');
    var sectionId = btn.getAttribute('data-section-id');
    if (!nextUrl || !sectionId) return;

    // The button carries its own `data-section-id` (so we know which
    // section to re-render), which means `btn.closest('[data-section-id]')`
    // would short-circuit to the button itself. Walk up via the
    // dd-collection-grid class instead — that selector is only on the
    // outer section.
    // The button carries its own `data-section-id` (so we know which
    // section to re-render), which means `btn.closest('[data-section-id]')`
    // would short-circuit to the button itself. Walk up via the
    // dd-collection-grid class — only the outer section has it.
    var section = btn.closest('.dd-collection-grid');
    var grid = section && section.querySelector('[data-grid]');
    if (!grid) return;

    // Build a same-origin URL with the section_id param appended, so
    // we get back just this section re-rendered at the next page.
    var url;
    try {
      url = new URL(nextUrl, window.location.origin);
    } catch (err) {
      return;
    }
    url.searchParams.set('section_id', sectionId);

    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-loading');

    fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Load more failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        appendNextPage(html, grid, btn);
      })
      .catch(function () {
        // On failure, clear the busy state so the user can retry.
      })
      .finally(function () {
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-loading');
      });
  }

  function appendNextPage(html, grid, btn) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');

    // The section rendering API returns either the raw section
    // fragment or a wrapping div depending on template — querying by
    // our stable hook covers both.
    var newGrid = doc.querySelector('[data-grid]');
    var newItems = newGrid ? newGrid.children : [];
    if (!newItems || newItems.length === 0) {
      // No items returned — we're at the end. Remove the button.
      removeButton(btn);
      return;
    }

    // Move nodes out of the parsed doc into our live grid.
    var frag = document.createDocumentFragment();
    // Convert to array because `children` is live and shrinks as we append.
    var arr = Array.prototype.slice.call(newItems);
    for (var i = 0; i < arr.length; i++) {
      frag.appendChild(arr[i]);
    }
    grid.appendChild(frag);

    // Update or retire the button based on whether the response
    // itself contains a load-more button (meaning `paginate.next`
    // was still true on the server side).
    var newBtn = doc.querySelector(BTN_SELECTOR);
    if (newBtn) {
      var next = newBtn.getAttribute('data-next-url');
      if (next) {
        btn.setAttribute('data-next-url', next);
      } else {
        removeButton(btn);
      }
    } else {
      removeButton(btn);
    }

    // Notify other scripts (rating hydrate, etc.) that new cards are
    // in the DOM. Keeping the event generic so future card enhancers
    // can subscribe without touching this file.
    document.dispatchEvent(
      new CustomEvent('dd:cards:appended', {
        bubbles: true,
        detail: { count: arr.length, section: grid.closest('[data-section-id]') },
      })
    );
  }

  function removeButton(btn) {
    var wrap = btn.closest('.dd-collection-grid__load-more-wrap') || btn;
    if (wrap && wrap.parentNode) wrap.parentNode.removeChild(wrap);
  }

  function init() {
    document.addEventListener('click', onClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
